import Phaser from 'phaser'

type SettingsMenuOptions = {
  pauseGameplay?: boolean
  showOpenButton?: boolean
  openButtonX?: number
  openButtonY?: number
}

export default class SettingsMenu {
  private scene: Phaser.Scene
  private pauseGameplay: boolean
  private showOpenButton: boolean
  private openButtonX: number
  private openButtonY: number
  private isMenuOpen = false

  private overlay: Phaser.GameObjects.Rectangle
  private panel: Phaser.GameObjects.Rectangle
  private titleText: Phaser.GameObjects.Text
  private audioLabelText: Phaser.GameObjects.Text
  private controlsLabelText: Phaser.GameObjects.Text
  private controlsText: Phaser.GameObjects.Text
  private volumeValueText: Phaser.GameObjects.Text
  private hintText: Phaser.GameObjects.Text
  private openButton: Phaser.GameObjects.Text
  private closeButton: Phaser.GameObjects.Text
  private volumeDownButton: Phaser.GameObjects.Text
  private volumeUpButton: Phaser.GameObjects.Text
  private escKey?: Phaser.Input.Keyboard.Key

  constructor(scene: Phaser.Scene, options: SettingsMenuOptions = {}) {
    this.scene = scene

    const { width, height } = this.scene.scale

    this.pauseGameplay = options.pauseGameplay ?? false
    this.showOpenButton = options.showOpenButton ?? true
    this.openButtonX = options.openButtonX ?? width - 16
    this.openButtonY = options.openButtonY ?? 16

    this.overlay = this.scene.add
      .rectangle(0, 0, width, height, 0x000000, 0.55)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(4000)
      .setVisible(false)
      .setInteractive()

    this.panel = this.scene.add
      .rectangle(width / 2, height / 2, 500, 500, 0x1f262d, 0.95)
      .setStrokeStyle(2, 0xd0d6dc)
      .setScrollFactor(0)
      .setDepth(4001)
      .setVisible(false)

    this.titleText = this.scene.add
      .text(width / 2, height / 2 - 205, 'SETTINGS', {
        fontFamily: 'monospace',
        fontSize: '30px',
        color: '#f2f6f8',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(4002)
      .setVisible(false)

    this.audioLabelText = this.scene.add
      .text(width / 2, height / 2 - 155, 'Audio', {
        fontFamily: 'monospace',
        fontSize: '18px',
        color: '#dbe3ea',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(4002)
      .setVisible(false)

    this.volumeDownButton = this.scene.add
      .text(width / 2 - 95, height / 2 - 112, '-', {
        fontFamily: 'monospace',
        fontSize: '34px',
        color: '#ffffff',
        backgroundColor: '#3b434a',
        padding: { x: 14, y: 6 },
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(4002)
      .setVisible(false)
      .setInteractive({ useHandCursor: true })

    this.volumeValueText = this.scene.add
      .text(width / 2, height / 2 - 112, '', {
        fontFamily: 'monospace',
        fontSize: '22px',
        color: '#ffffff',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(4002)
      .setVisible(false)

    this.volumeUpButton = this.scene.add
      .text(width / 2 + 95, height / 2 - 112, '+', {
        fontFamily: 'monospace',
        fontSize: '34px',
        color: '#ffffff',
        backgroundColor: '#3b434a',
        padding: { x: 14, y: 6 },
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(4002)
      .setVisible(false)
      .setInteractive({ useHandCursor: true })

    this.controlsLabelText = this.scene.add
      .text(width / 2, height / 2 - 58, 'Controls', {
        fontFamily: 'monospace',
        fontSize: '18px',
        color: '#dbe3ea',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(4002)
      .setVisible(false)

    this.controlsText = this.scene.add
      .text(
        width / 2,
        height / 2 - 30,
        [
          'Move: Arrow Keys / A D',
          'Jump: W / Up / Space',
          'Build: Left Click',
          'Remove Build Tile: Right Click',
          'Reset Level: R',
        ].join('\n'),
        {
          fontFamily: 'monospace',
          fontSize: '17px',
          color: '#dbe3ea',
          align: 'center',
        },
      )
      .setOrigin(0.5, 0)
      .setScrollFactor(0)
      .setDepth(4002)
      .setVisible(false)

    this.closeButton = this.scene.add
      .text(width / 2, height / 2 + 145, 'CLOSE', {
        fontFamily: 'monospace',
        fontSize: '24px',
        color: '#ffffff',
        backgroundColor: '#3b434a',
        padding: { x: 16, y: 8 },
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(4002)
      .setVisible(false)
      .setInteractive({ useHandCursor: true })

    this.hintText = this.scene.add
      .text(width / 2, height / 2 + 188, 'Press ESC to toggle settings', {
        fontFamily: 'monospace',
        fontSize: '15px',
        color: '#b8c2cc',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(4002)
      .setVisible(false)

    this.openButton = this.scene.add
      .text(this.openButtonX, this.openButtonY, '⚙', {
        fontFamily: 'monospace',
        fontSize: '28px',
        color: '#ffffff',
        backgroundColor: '#29313a',
        padding: { x: 10, y: 2 },
      })
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(3000)
      .setVisible(this.showOpenButton)

    if (this.showOpenButton) {
      this.openButton.setInteractive({ useHandCursor: true })
      this.openButton.on('pointerdown', () => {
        this.toggle()
      })
    }

    this.closeButton.on('pointerdown', () => {
      this.close()
    })

    this.volumeDownButton.on('pointerdown', () => {
      this.adjustVolume(-0.1)
    })

    this.volumeUpButton.on('pointerdown', () => {
      this.adjustVolume(0.1)
    })

    if (this.scene.input.keyboard) {
      this.escKey = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ESC)
      this.escKey.on('down', () => {
        this.toggle()
      })
    }

    this.refreshVolumeText()

    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.destroy()
    })
  }

  isOpen(): boolean {
    return this.isMenuOpen
  }

  toggleMenu(): void {
    this.toggle()
  }

  private adjustVolume(delta: number): void {
    const nextVolume = Phaser.Math.Clamp(this.scene.sound.volume + delta, 0, 1)
    this.scene.sound.setVolume(nextVolume)
    this.refreshVolumeText()
  }

  private refreshVolumeText(): void {
    this.volumeValueText.setText(`Volume: ${Math.round(this.scene.sound.volume * 100)}%`)
  }

  private toggle(): void {
    if (this.isMenuOpen) {
      this.close()
      return
    }

    this.open()
  }

  private open(): void {
    this.isMenuOpen = true
    this.refreshVolumeText()

    this.overlay.setVisible(true)
    this.panel.setVisible(true)
    this.titleText.setVisible(true)
    this.audioLabelText.setVisible(true)
    this.controlsLabelText.setVisible(true)
    this.controlsText.setVisible(true)
    this.volumeDownButton.setVisible(true)
    this.volumeValueText.setVisible(true)
    this.volumeUpButton.setVisible(true)
    this.closeButton.setVisible(true)
    this.hintText.setVisible(true)

    if (this.pauseGameplay) {
      this.scene.physics.world.pause()
    }
  }

  private close(): void {
    this.isMenuOpen = false

    this.overlay.setVisible(false)
    this.panel.setVisible(false)
    this.titleText.setVisible(false)
    this.audioLabelText.setVisible(false)
    this.controlsLabelText.setVisible(false)
    this.controlsText.setVisible(false)
    this.volumeDownButton.setVisible(false)
    this.volumeValueText.setVisible(false)
    this.volumeUpButton.setVisible(false)
    this.closeButton.setVisible(false)
    this.hintText.setVisible(false)

    if (this.pauseGameplay) {
      this.scene.physics.world.resume()
    }
  }

  destroy(): void {
    if (this.escKey) {
      this.escKey.removeAllListeners()
    }

    this.overlay.destroy()
    this.panel.destroy()
    this.titleText.destroy()
    this.audioLabelText.destroy()
    this.controlsLabelText.destroy()
    this.controlsText.destroy()
    this.volumeDownButton.destroy()
    this.volumeValueText.destroy()
    this.volumeUpButton.destroy()
    this.closeButton.destroy()
    this.hintText.destroy()
    this.openButton.destroy()
  }
}
